'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import { formatCategoryForDisplay } from '../../../utils/category-utils';
import { LabelDefault } from '../../../components/typography';
import { TableGrid, type TableGridRow } from '../../table-grid/TableGrid';
import { toasterApi } from '../../toast/ToastProvider';
import { WizardActionDialog } from '../../wizard-action-dialog/WizardActionDialog';
import { TableSwitch } from '../TableSwitch';
import {
  getMcpServerScopeCategorySections,
  getSignalCategoriesForTarget,
  orderMcpServerScopeCategories,
  type SignalCategoryTargetType,
} from './signal-category-scope';

export type SignalCategoriesContentProps = {
  signalCategoriesInScope: SignalCategoryType[];
  onSignalCategoriesChange: (
    signalCategoriesInScope: SignalCategoryType[],
  ) => void;
  targetType?: SignalCategoryTargetType;
  isReadOnly?: boolean;
  requireDisableConfirmation?: boolean;
  tableMaxHeight?: string;
};

export const SignalCategoriesContent = ({
  signalCategoriesInScope,
  targetType = 'WEB_APPLICATION',
  isReadOnly,
  requireDisableConfirmation,
  tableMaxHeight = '300px',
  onSignalCategoriesChange,
}: SignalCategoriesContentProps) => {
  const t = useTranslations('designSystem.settings');
  const tCommon = useTranslations('designSystem.common');
  const [pendingDisableCategory, setPendingDisableCategory] =
    useState<SignalCategoryType | null>(null);
  const [pendingDisableAll, setPendingDisableAll] = useState(false);

  const scopedCategories = useMemo(
    () => getSignalCategoriesForTarget({ targetType }),
    [targetType],
  );

  const orderedVisibleCategories = useMemo(() => {
    const selectedOutsideScope = signalCategoriesInScope.filter(
      (category) => !scopedCategories.includes(category),
    );
    const combined = [...scopedCategories, ...selectedOutsideScope];

    if (targetType !== 'MCP_SERVER') {
      return combined;
    }

    return orderMcpServerScopeCategories(combined);
  }, [scopedCategories, signalCategoriesInScope, targetType]);

  const allInScope = orderedVisibleCategories.every((category) =>
    signalCategoriesInScope.includes(category),
  );

  const updateSignalCategoryScope = (category: SignalCategoryType) => {
    onSignalCategoriesChange(
      signalCategoriesInScope.includes(category)
        ? signalCategoriesInScope.filter(
            (signalCategory) => signalCategory !== category,
          )
        : [...signalCategoriesInScope, category],
    );
  };

  const handleToggleInScope = (category: SignalCategoryType) => {
    if (isReadOnly) {
      return;
    }

    const isCategoryInScope = signalCategoriesInScope.includes(category);

    if (requireDisableConfirmation && isCategoryInScope) {
      setPendingDisableCategory(category);

      return;
    }

    updateSignalCategoryScope(category);
  };

  const handleToggleAllInScope = (checked: boolean) => {
    if (isReadOnly) {
      return;
    }

    if (checked) {
      onSignalCategoriesChange([...orderedVisibleCategories]);

      return;
    }

    if (requireDisableConfirmation && signalCategoriesInScope.length > 0) {
      setPendingDisableAll(true);

      return;
    }

    onSignalCategoriesChange([]);
  };

  const handleDisableConfirmationDialogOpenChange = (open: boolean) => {
    if (!open) {
      setPendingDisableCategory(null);
      setPendingDisableAll(false);
    }
  };

  const handleDisableConfirmation = () => {
    if (pendingDisableAll) {
      onSignalCategoriesChange([]);
      setPendingDisableAll(false);
      toasterApi.success({
        title: t('signalCategories.disableSuccessToast.title', {
          count: orderedVisibleCategories.length,
        }),
      });

      return;
    }

    if (!pendingDisableCategory) {
      return;
    }

    updateSignalCategoryScope(pendingDisableCategory);
    setPendingDisableCategory(null);
    toasterApi.success({
      title: t('signalCategories.disableSuccessToast.title', {
        count: 1,
      }),
    });
  };

  const buildRows = () => {
    const { httpTransportCategories } = getMcpServerScopeCategorySections();
    const httpTransportCategorySet = new Set(httpTransportCategories);
    const httpTransportSectionLabel = t(
      'signalCategories.httpTransportSection',
    );
    let httpTransportSectionInserted = false;

    return orderedVisibleCategories.flatMap<TableGridRow>((category) => {
      const isInScope = signalCategoriesInScope.includes(category);
      const categoryRow: TableGridRow = {
        id: category,
        cells: [
          formatCategoryForDisplay(category),
          <TableSwitch
            key={`${category}-${pendingDisableCategory?.toString() ?? ''}`}
            checked={isInScope}
            onCheckedChange={() => handleToggleInScope(category)}
            disabled={isReadOnly}
          >
            {isInScope
              ? t('signalCategories.grid.yes')
              : t('signalCategories.grid.no')}
          </TableSwitch>,
        ],
        value: category,
        ...(targetType === 'MCP_SERVER' &&
          httpTransportCategorySet.has(category) && {
            groupId: 'mcp-http-transport',
          }),
      };

      if (
        targetType !== 'MCP_SERVER' ||
        !httpTransportCategorySet.has(category) ||
        httpTransportSectionInserted
      ) {
        return [categoryRow];
      }

      httpTransportSectionInserted = true;

      return [
        {
          id: 'mcp-http-transport-section',
          variant: 'subtle',
          value: httpTransportCategories.join(' '),
          cells: [
            <LabelDefault key="mcp-http-transport-section-label">
              {httpTransportSectionLabel}
            </LabelDefault>,
            null,
          ],
        },
        categoryRow,
      ];
    });
  };

  const rows = buildRows();

  return (
    <>
      <TableGrid
        tableMaxHeight={tableMaxHeight}
        columns={[
          {
            id: 'name',
            title: t('signalCategories.grid.columns.name'),
          },
          {
            id: 'inScope',
            title: (
              <TableSwitch
                flexDirection="row-reverse"
                justifyContent="flex-end"
                checked={allInScope}
                disabled={isReadOnly}
                onCheckedChange={(details) =>
                  handleToggleAllInScope(details.checked === true)
                }
              >
                {t('signalCategories.grid.columns.inScope')}
              </TableSwitch>
            ),
          },
        ]}
        rows={rows}
        placeholder={t('signalCategories.searchPlaceholder')}
      />
      <WizardActionDialog
        open={Boolean(pendingDisableCategory) || pendingDisableAll}
        title={t('signalCategories.disableConfirmationDialog.title', {
          count: pendingDisableAll ? orderedVisibleCategories.length : 1,
        })}
        description={t(
          'signalCategories.disableConfirmationDialog.description',
          {
            count: pendingDisableAll ? orderedVisibleCategories.length : 1,
          },
        )}
        action={tCommon('yes')}
        cancelText={tCommon('cancel')}
        onOpenChange={handleDisableConfirmationDialogOpenChange}
        onActionClick={handleDisableConfirmation}
      />
    </>
  );
};
